/**
 * CDOT traveller information: highway incidents and road conditions.
 *
 * The one genuinely live feed on this map. Denver's crime data publishes days
 * behind and permits are static; this updates continuously, so it is the only
 * layer where "live" is literally true.
 *
 * Statewide at source and clipped to Denver on the server — most of what CDOT
 * publishes is I-70 in the mountains, which is not this map's business.
 */

import { PathLayer, ScatterplotLayer } from "@deck.gl/layers";

import { GROUND_LIFT_M } from "../lib/geo.js";
import { Layer } from "./base.js";
import { palette, themeName } from "../theme/palette.js";
import { OVER_EVERYTHING } from "../map/depth.js";

/** Sharp red-orange: this is the only thing here happening right now. */

/** A shut road is the one thing here you must not miss. */

export class CotripLayer extends Layer {
  static key = "cotrip";
  static label = "Highways";
  static description = "CDOT incidents and road conditions, live";

  /**
   * Matched to the server's 10-minute cache. A crash closing I-25 is the
   * clearest case on this map of something you want to learn about without
   * reloading, and it is the fastest-moving source we have.
   */
  static refreshMs = 10 * 60 * 1000;

  async load() {
    this.status = "loading";
    try {
      const response = await fetch(`${this.basePath}/cotrip`);
      if (!response.ok) throw new Error(String(response.status));
      const body = await response.json();
      this._incidents = body.incidents ?? [];
      this._conditions = body.conditions ?? [];

      if (body.configured === false) {
        this.status = "unavailable";
        this.detail = "No API key set";
        return null;
      }
      const total = this._incidents.length + this._conditions.length;
      this.status = total ? "ready" : "unavailable";
      const closed = [...this._incidents, ...this._conditions]
        .filter((r) => r.closed).length;
      this.detail = closed
        ? `${closed} closed, ${this._incidents.length} incidents`
        : (total ? `${this._incidents.length} incidents` : "All clear");
      return null;
    } catch (error) {
      this.status = "unavailable";
      this.detail = `Unavailable (${error.message})`;
      return null;
    }
  }

  get loadedCount() {
    return (this._incidents?.length ?? 0) + (this._conditions?.length ?? 0);
  }

  legend() {
    return [
      { key: "closed", rgb: palette().cotripClosed, label: "Road closed", note: "Every lane shut, per CDOT. Live." },
      { key: "incident", rgb: palette().cotripIncident, label: "Highway incident", note: "CDOT, updated live." },
      { key: "condition", rgb: palette().cotripCondition, label: "Road condition", note: "CDOT reported surface conditions." },
    ];
  }

  describe(row) {
    // Closure runs and road conditions both carry a path; the closure knows
    // its own incident type, a condition does not.
    if (row.path && row.kind) {
      const rows = [
        ["Status", "Road closed — all lanes"],
        ["Type", String(row.kind).replace(/_/g, " ")],
      ];
      if (row.route) rows.push(["Route", row.route]);
      if (row.message) rows.push(["Detail", row.message]);
      rows.push(["Source", "CDOT COTrip"]);
      return { title: row.route || "Road closed", rows };
    }
    if (row.path) {
      return {
        title: row.closed ? "Road closed" : (row.name || row.route || "Road condition"),
        rows: [
          ["Status", row.closed ? "Closed" : "Open"],
          ["Condition", row.condition || "—"],
          ["Route", row.route || "—"],
          ["Source", "CDOT COTrip"],
        ],
      };
    }
    const rows = [["Type", String(row.kind ?? "incident").replace(/_/g, " ")]];
    if (row.closed) rows.unshift(["Status", "Road closed — all lanes"]);
    if (row.route) rows.push(["Route", row.route]);
    if (row.message) rows.push(["Detail", row.message]);
    if (row.started) rows.push(["Since", String(row.started).replace("T", " ").slice(0, 16)]);
    rows.push(["Source", "CDOT COTrip"]);
    return { title: row.route || "Highway incident", rows };
  }

  render() {
    if (!this.enabled || this.status !== "ready") {
      this.visibleCount = 0;
      return [];
    }
    // A closure is a stretch of road, so it draws as one — the same line road
    // work uses, in red. Only closures without an extent fall back to a dot.
    //
    // "Closed" is a state, not a dataset: it cuts across both the incident
    // feed and the condition feed, and both draw red when it is set. So the
    // legend key is read off `closed` first, and only what is left over is
    // sorted by which feed it came from — which is exactly how the three
    // legend rows describe themselves.
    const hidden = (key) => this.isHidden(key);
    const incidents = (this._incidents ?? []).filter((d) =>
      !hidden(d.closed ? "closed" : "incident"));
    const conditions = (this._conditions ?? []).filter((d) =>
      !hidden(d.closed ? "closed" : "condition"));
    const closedRuns = incidents.filter((d) => d.closed && d.path?.length > 1);
    const points = incidents.filter((d) => !(d.closed && d.path?.length > 1));
    this.visibleCount = incidents.length + conditions.length;

    return [
      new PathLayer({
        id: `${this.key}-closed`,
        data: closedRuns,
        getPath: (d) => d.path.map(([x, y]) => [x, y, GROUND_LIFT_M]),
        getColor: [...palette().cotripClosed, 240],
        // Heavier than road work: this is the one thing on the map that means
        // you cannot get through.
        getWidth: 9,
        widthUnits: "meters",
        widthMinPixels: 3,
        widthMaxPixels: 18,
        capRounded: true,
        jointRounded: true,
        pickable: true,
        parameters: OVER_EVERYTHING,
      }),
      new PathLayer({
        id: `${this.key}-conditions`,
        data: conditions,
        getPath: (d) => d.path.map(([x, y]) => [x, y, GROUND_LIFT_M]),
        // Closed stretches draw red and noticeably heavier: this is the one
        // thing on the layer that changes whether you can get somewhere.
        getColor: (d) => (d.closed ? [...palette().cotripClosed, 235] : [...palette().cotripCondition, 150]),
        updateTriggers: { getColor: themeName() },
        getWidth: (d) => (d.closed ? 9 : 6),
        widthUnits: "meters",
        widthMinPixels: 1.5,
        widthMaxPixels: 16,
        capRounded: true,
        jointRounded: true,
        pickable: true,
        parameters: OVER_EVERYTHING,
      }),
      new ScatterplotLayer({
        id: `${this.key}-incidents`,
        data: points,
        getPosition: (d) => [d.lng, d.lat],
        getRadius: (d) => (d.closed ? 70 : 40),
        radiusUnits: "meters",
        radiusMinPixels: 4,
        radiusMaxPixels: 20,
        stroked: true,
        filled: true,
        lineWidthUnits: "pixels",
        getLineWidth: (d) => (d.closed ? 2.5 : 1.5),
        getFillColor: (d) => (d.closed ? [...palette().cotripClosed, 190] : [...palette().cotripIncident, 165]),
        getLineColor: (d) => (d.closed ? [...palette().cotripClosed, 255] : [...palette().cotripIncident, 240]),
        updateTriggers: { getFillColor: themeName(), getLineColor: themeName() },
        pickable: true,
        parameters: OVER_EVERYTHING,
      }),
    ];
  }
}
